import React, { useState, useRef } from 'react';
import axios from 'axios';
import './InventorySync.css';
import { API_BASE_URL } from '../config/api';

interface ComparisonResult {
  batchNumber: string;
  productNumber1?: string;
  productNumber2?: string;
  table1Quantity: number;
  table2Quantity: number;
  difference: number;
  greenCard1?: string;
  greenCard2?: string;
  greenCardMatch?: boolean;
  status: 'match' | 'quantity_mismatch' | 'green_card_mismatch' | 'missing_in_table1' | 'missing_in_table2';
  zohoItemId?: string;
}

interface SyncResult {
  totalItems: number;
  matchingItems: number;
  mismatchedItems: number;
  missingInTable1: number;
  missingInTable2: number;
  comparisons: ComparisonResult[];
  summary: {
    exactMatches: number;
    quantityMismatches: number;
    greenCardMismatches: number;
    missingItems: number;
  };
}

type FilterType = 'all' | 'match' | 'quantity_mismatch' | 'green_card_mismatch' | 'missing_items';

const InventorySync: React.FC = () => {
  const [table1File, setTable1File] = useState<File | null>(null);
  const [table2File, setTable2File] = useState<File | null>(null);
  const [table1ItemNameColumn, setTable1ItemNameColumn] = useState('');
  const [table1QuantityColumn, setTable1QuantityColumn] = useState('');
  const [table1BatchColumn, setTable1BatchColumn] = useState('');
  const [table1GreenCardColumn, setTable1GreenCardColumn] = useState('');
  const [table2ItemNameColumn, setTable2ItemNameColumn] = useState('');
  const [table2QuantityColumn, setTable2QuantityColumn] = useState('');
  const [table2BatchColumn, setTable2BatchColumn] = useState('');
  const [table2GreenCardColumn, setTable2GreenCardColumn] = useState('');
  const [compareGreenCard, setCompareGreenCard] = useState(false);
  const [trackBatches, setTrackBatches] = useState(false);
  const [tolerance, setTolerance] = useState(0);
  const [loading, setLoading] = useState(false);
  const [comparisonResult, setComparisonResult] = useState<SyncResult | null>(null);
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [loadingZohoLinks, setLoadingZohoLinks] = useState(false);
  
  
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  
  
  const [copyMessage, setCopyMessage] = useState('');
  
  
  const [table1Columns, setTable1Columns] = useState<string[]>([]);
  const [table2Columns, setTable2Columns] = useState<string[]>([]);

  const file1Ref = useRef<HTMLInputElement>(null);
  const file2Ref = useRef<HTMLInputElement>(null);

  const handleFileChange = async (file: File | null, setFile: (file: File | null) => void, setColumns: (columns: string[]) => void, isTable1: boolean) => {
    setError('');
    
    if (file) {
      if (!file.name.toLowerCase().endsWith('.xlsx') && !file.name.toLowerCase().endsWith('.xls')) {
        setError('Моля, изберете Excel файл (.xlsx или .xls)');
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        setError('Файлът е твърде голям. Максималният размер е 50MB');
        return;
      }

      if (file.size === 0) {
        setError('Файлът е празен');
        return;
      }
    }
    
    setFile(file);
    setError('');
    
    if (file) {
      try {
        const fileData = await convertFileToBase64(file);
        const response = await axios.post(`${API_BASE_URL}/v1/inventory-sync/get-columns`, {
          fileData
        });
        const columns = response.data.columns;
        setColumns(columns);
        
        
        if (file.name) {
          applyColumnSelections(file.name, isTable1);
        }
      } catch (err: any) {
        console.error('Error getting column names:', err);
        
        let errorMessage = 'Грешка при четене на колоните от файла';
        
        if (err.response?.data?.message) {
          errorMessage = err.response.data.message;
        } else if (err.response?.data?.technical) {
          errorMessage = `Грешка при четене на файла: ${err.response.data.technical}`;
        } else if (err.message) {
          errorMessage = `Грешка при четене на файла: ${err.message}`;
        }
        
        setError(errorMessage);
      }
    } else {
      setColumns([]);
    }
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!file) {
        reject(new Error('Няма избран файл'));
        return;
      }

      const validTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/octet-stream'
      ];
      
      if (!validTypes.includes(file.type) && !file.name.toLowerCase().match(/\.(xlsx?|xls)$/)) {
        reject(new Error('Невалиден формат на файл. Моля, изберете Excel файл (.xlsx или .xls)'));
        return;
      }

      if (file.size > 50 * 1024 * 1024) {
        reject(new Error('Файлът е твърде голям. Максималният размер е 50MB'));
        return;
      }

      const reader = new FileReader();
      
      reader.onload = () => {
        try {
          const base64 = reader.result as string;
          if (!base64 || typeof base64 !== 'string') {
            reject(new Error('Грешка при четене на файла'));
            return;
          }
          
          const base64Data = base64.split(',')[1];
          if (!base64Data) {
            reject(new Error('Грешка при конвертиране на файла'));
            return;
          }
          
          console.log('File converted to base64, size:', base64Data.length);
          resolve(base64Data);
        } catch (error) {
          reject(new Error('Грешка при обработка на файла'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Грешка при четене на файла. Моля, опитайте отново.'));
      };

      reader.readAsDataURL(file);
    });
  };

  
  const getFilteredComparisons = () => {
    if (!comparisonResult) return [];
    
    switch (activeFilter) {
      case 'match':
        return comparisonResult.comparisons.filter(c => c.status === 'match');
      case 'quantity_mismatch':
        return comparisonResult.comparisons.filter(c => c.status === 'quantity_mismatch');
      case 'green_card_mismatch':
        return comparisonResult.comparisons.filter(c => c.status === 'green_card_mismatch');
      case 'missing_items':
        return comparisonResult.comparisons.filter(c => 
          c.status === 'missing_in_table1' || c.status === 'missing_in_table2'
        );
      default:
        return comparisonResult.comparisons;
    }
  };

  
  const handleFilterClick = (filterType: FilterType) => {
    setActiveFilter(activeFilter === filterType ? 'all' : filterType);
  };

  
  const getSummaryCardClassName = (filterType: FilterType) => {
    const baseClass = 'summary-card';
    const isActive = activeFilter === filterType;
    
    switch (filterType) {
      case 'match':
        return `${baseClass} match ${isActive ? 'active' : ''}`;
      case 'quantity_mismatch':
        return `${baseClass} mismatch ${isActive ? 'active' : ''}`;
      case 'green_card_mismatch':
        return `${baseClass} green-card-mismatch ${isActive ? 'active' : ''}`;
      case 'missing_items':
        return `${baseClass} missing ${isActive ? 'active' : ''}`;
      default:
        return `${baseClass} ${isActive ? 'active' : ''}`;
    }
  };

  const handleCompare = async () => {
    if (!table1File || !table2File) {
      setError('Моля, изберете и двата Excel файла');
      return;
    }

    if (!table1ItemNameColumn || !table2ItemNameColumn) {
      setError('Моля, посочете колоните за име на артикул за двата файла');
      return;
    }

    if (!table1QuantityColumn || !table2QuantityColumn) {
      setError('Моля, посочете колоните за количество за двата файла');
      return;
    }

    if (trackBatches && (!table1BatchColumn || !table2BatchColumn)) {
      setError('Моля, посочете колоните за номер на партида за двата файла когато проследяването на партиди е включено');
      return;
    }

    if (compareGreenCard && (!table1GreenCardColumn || !table2GreenCardColumn)) {
      setError('Моля, посочете колоните за зелена карта за двата файла когато сравнението е включено');
      return;
    }

    setLoading(true);
    setError('');
    setActiveFilter('all');

    try {
      const table1Data = await convertFileToBase64(table1File);
      const table2Data = await convertFileToBase64(table2File);

      const table1Name = table1File?.name || 'Таблица 1';
      const table2Name = table2File?.name || 'Таблица 2';
      
      const response = await axios.post(`${API_BASE_URL}/v1/inventory-sync/compare`, {
        table1Data,
        table2Data,
        table1Name,
        table2Name,
        table1ItemNameColumn,
        table1QuantityColumn,
        table1BatchColumn: trackBatches ? table1BatchColumn : undefined,
        table1GreenCardColumn: compareGreenCard ? table1GreenCardColumn : undefined,
        table2ItemNameColumn,
        table2QuantityColumn,
        table2BatchColumn: trackBatches ? table2BatchColumn : undefined,
        table2GreenCardColumn: compareGreenCard ? table2GreenCardColumn : undefined,
        compareGreenCard,
        trackBatches,
        tolerance: Number(tolerance)
      });

      const result = response.data.result;
      setComparisonResult(result);
      
      
      

    } catch (err: any) {
      console.error('Error comparing inventories:', err);
      setError(err.response?.data?.message || 'Грешка при сравняване на инвентарите');
    } finally {
      setLoading(false);
    }
  };

  const fetchZohoItemIds = async (result: SyncResult) => {
    setLoadingZohoLinks(true);
    try {
      const productNumbers = new Set<string>();
      result.comparisons.forEach(comp => {
        if (comp.productNumber1) productNumbers.add(comp.productNumber1);
        if (comp.productNumber2) productNumbers.add(comp.productNumber2);
      });

      if (productNumbers.size > 0) {
        const response = await axios.post(`${API_BASE_URL}/v1/inventory-sync/get-zoho-item-ids`, {
          productNumbers: Array.from(productNumbers)
        });

        const itemIdMap = response.data.itemIds;
        
        const updatedComparisons = result.comparisons.map(comp => ({
          ...comp,
          zohoItemId: itemIdMap[comp.productNumber1 || comp.productNumber2 || ''] || null
        }));

        setComparisonResult({
          ...result,
          comparisons: updatedComparisons
        });
      }
    } catch (err: any) {
      console.warn('Could not fetch Zoho item IDs:', err.response?.data?.message || err.message);
    } finally {
      setLoadingZohoLinks(false);
    }
  };

  const handleExport = async () => {
    if (!comparisonResult) {
      setError('Няма резултати за експортиране');
      return;
    }

    setExporting(true);
    setError('');

    try {
      const table1Name = table1File?.name || 'Таблица 1';
      const table2Name = table2File?.name || 'Таблица 2';
      
      const filteredComparisons = getFilteredComparisons();
      
      const response = await axios.post(`${API_BASE_URL}/v1/inventory-sync/export`, {
        comparisonResult: {
          ...comparisonResult,
          comparisons: filteredComparisons
        },
        table1Name,
        table2Name,
        filterType: activeFilter,
        trackBatches
      }, {
        responseType: 'blob'
      });

      const filterSuffix = activeFilter !== 'all' ? `-${activeFilter}` : '';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `инвентар-сравнение${filterSuffix}-${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Error exporting comparison:', err);
      setError('Грешка при експортиране на сравнението');
    } finally {
      setExporting(false);
    }
  };

  const getStatusColor = (status: ComparisonResult['status']) => {
    switch (status) {
      case 'match': return 'green';
      case 'quantity_mismatch': return 'red';
      case 'green_card_mismatch': return 'orange';
      case 'missing_in_table1':
      case 'missing_in_table2': return 'purple';
      default: return 'gray';
    }
  };

  const getStatusText = (status: ComparisonResult['status']) => {
    switch (status) {
      case 'match': return 'Съвпадение';
      case 'quantity_mismatch': return 'Разлика в количеството';
      case 'green_card_mismatch': return 'Разлика в зелена карта';
      case 'missing_in_table1': return 'Липсва в Таблица 1';
      case 'missing_in_table2': return 'Липсва в Таблица 2';
      default: return status;
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage('Копирано в клипборда!');
      setTimeout(() => setCopyMessage(''), 3000);
    } catch (err) {
      
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopyMessage('Копирано в клипборда!');
      setTimeout(() => setCopyMessage(''), 3000);
    }
  };

  
  const saveColumnSelections = (fileName: string, selections: any) => {
    try {
      const key = `columnSelections_${fileName}`;
      localStorage.setItem(key, JSON.stringify(selections));
    } catch (err) {
      console.warn('Could not save column selections to localStorage:', err);
    }
  };

  const loadColumnSelections = (fileName: string) => {
    try {
      const key = `columnSelections_${fileName}`;
      const saved = localStorage.getItem(key);
      return saved ? JSON.parse(saved) : null;
    } catch (err) {
      console.warn('Could not load column selections from localStorage:', err);
      return null;
    }
  };

  const applyColumnSelections = (fileName: string, isTable1: boolean) => {
    const saved = loadColumnSelections(fileName);
    if (saved) {
      if (isTable1) {
        if (saved.itemNameColumn) setTable1ItemNameColumn(saved.itemNameColumn);
        if (saved.quantityColumn) setTable1QuantityColumn(saved.quantityColumn);
        if (saved.batchColumn) setTable1BatchColumn(saved.batchColumn);
        if (saved.greenCardColumn) setTable1GreenCardColumn(saved.greenCardColumn);
      } else {
        if (saved.itemNameColumn) setTable2ItemNameColumn(saved.itemNameColumn);
        if (saved.quantityColumn) setTable2QuantityColumn(saved.quantityColumn);
        if (saved.batchColumn) setTable2BatchColumn(saved.batchColumn);
        if (saved.greenCardColumn) setTable2GreenCardColumn(saved.greenCardColumn);
      }
      console.log(`Applied cached column selections for ${fileName}:`, saved);
    }
  };

  const saveCurrentSelections = (fileName: string, isTable1: boolean) => {
    if (!fileName) return;
    
    const selections = isTable1 ? {
      itemNameColumn: table1ItemNameColumn,
      quantityColumn: table1QuantityColumn,
      batchColumn: table1BatchColumn,
      greenCardColumn: table1GreenCardColumn
    } : {
      itemNameColumn: table2ItemNameColumn,
      quantityColumn: table2QuantityColumn,
      batchColumn: table2BatchColumn,
      greenCardColumn: table2GreenCardColumn
    };
    
    saveColumnSelections(fileName, selections);
  };

  return (
    <div className="inventory-sync">
      <div className="sync-header">
        <h2>Синхронизация на Инвентар</h2>
        <p>Качете два Excel файла за сравняване на количества по име на артикул</p>
      </div>

      <div className="sync-config">
        <div className="files-container">
          <div className="file-column">
            <h3>Файл 1</h3>
            
            <div className="step-section">
              <div className="step-header">
                <span className="step-number">1</span>
                <label>Качете първи Excel файл:</label>
              </div>
              <input
                type="file"
                ref={file1Ref}
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null, setTable1File, setTable1Columns, true)}
                className="file-input"
              />
              {table1File && (
                <div className="file-info">
                  <span className="file-name">{table1File.name}</span>
                  {table1Columns.length > 0 && (
                    <span className="column-count">({table1Columns.length} колони)</span>
                  )}
                </div>
              )}
            </div>

            {table1Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">2</span>
                  <label>Изберете колона за име на артикул:</label>
                </div>
                <select
                  value={table1ItemNameColumn}
                  onChange={(e) => {
                    setTable1ItemNameColumn(e.target.value);
                    if (table1File) saveCurrentSelections(table1File.name, true);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table1Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}

            {table1Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">3</span>
                  <label>Изберете колона за количество:</label>
                </div>
                <select
                  value={table1QuantityColumn}
                  onChange={(e) => {
                    setTable1QuantityColumn(e.target.value);
                    if (table1File) saveCurrentSelections(table1File.name, true);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table1Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}

            {trackBatches && table1Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">4</span>
                  <label>Изберете колона за номер на партида:</label>
                </div>
                <select
                  value={table1BatchColumn}
                  onChange={(e) => {
                    setTable1BatchColumn(e.target.value);
                    if (table1File) saveCurrentSelections(table1File.name, true);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table1Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}

            {compareGreenCard && table1Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">{trackBatches ? '5' : '4'}</span>
                  <label>Изберете колона за зелена карта:</label>
                </div>
                <select
                  value={table1GreenCardColumn}
                  onChange={(e) => {
                    setTable1GreenCardColumn(e.target.value);
                    if (table1File) saveCurrentSelections(table1File.name, true);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table1Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="file-column">
            <h3>Файл 2</h3>
            
            <div className="step-section">
              <div className="step-header">
                <span className="step-number">1</span>
                <label>Качете втори Excel файл:</label>
              </div>
              <input
                type="file"
                ref={file2Ref}
                accept=".xlsx,.xls"
                onChange={(e) => handleFileChange(e.target.files?.[0] || null, setTable2File, setTable2Columns, false)}
                className="file-input"
              />
              {table2File && (
                <div className="file-info">
                  <span className="file-name">{table2File.name}</span>
                  {table2Columns.length > 0 && (
                    <span className="column-count">({table2Columns.length} колони)</span>
                  )}
                </div>
              )}
            </div>

            {table2Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">2</span>
                  <label>Изберете колона за име на артикул:</label>
                </div>
                <select
                  value={table2ItemNameColumn}
                  onChange={(e) => {
                    setTable2ItemNameColumn(e.target.value);
                    if (table2File) saveCurrentSelections(table2File.name, false);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table2Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}

            {table2Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">3</span>
                  <label>Изберете колона за количество:</label>
                </div>
                <select
                  value={table2QuantityColumn}
                  onChange={(e) => {
                    setTable2QuantityColumn(e.target.value);
                    if (table2File) saveCurrentSelections(table2File.name, false);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table2Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}

            {trackBatches && table2Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">4</span>
                  <label>Изберете колона за номер на партида:</label>
                </div>
                <select
                  value={table2BatchColumn}
                  onChange={(e) => {
                    setTable2BatchColumn(e.target.value);
                    if (table2File) saveCurrentSelections(table2File.name, false);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table2Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}

            {compareGreenCard && table2Columns.length > 0 && (
              <div className="step-section">
                <div className="step-header">
                  <span className="step-number">{trackBatches ? '5' : '4'}</span>
                  <label>Изберете колона за зелена карта:</label>
                </div>
                <select
                  value={table2GreenCardColumn}
                  onChange={(e) => {
                    setTable2GreenCardColumn(e.target.value);
                    if (table2File) saveCurrentSelections(table2File.name, false);
                  }}
                  className="column-select"
                >
                  <option value="">Изберете колона...</option>
                  {table2Columns.map((column) => (
                    <option key={column} value={column}>{column}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        </div>

        <div className="comparison-options">
          <h3>Опции за Сравнение</h3>
          <div className="options-container">
            <div className="option-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={trackBatches}
                  onChange={(e) => setTrackBatches(e.target.checked)}
                />
                Проследяване на партиди
              </label>
              <p className="option-hint">
                Когато е включено, артикули с еднакво име но различни партиди ще бъдат показани отделно
              </p>
            </div>
            
            <div className="option-item">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={compareGreenCard}
                  onChange={(e) => setCompareGreenCard(e.target.checked)}
                />
                Сравняване на полето Зелена Карта
              </label>
            </div>
            
            <div className="option-item">
              <label>Толерантност (позволява малки разлики в количеството):</label>
              <input
                type="number"
                min="0"
                value={tolerance}
                onChange={(e) => setTolerance(Number(e.target.value))}
                placeholder="0"
                className="tolerance-input"
              />
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button
            className="compare-button"
            onClick={handleCompare}
            disabled={loading || !table1File || !table2File || !table1ItemNameColumn || !table2ItemNameColumn || !table1QuantityColumn || !table2QuantityColumn || (trackBatches && (!table1BatchColumn || !table2BatchColumn)) || (compareGreenCard && (!table1GreenCardColumn || !table2GreenCardColumn))}
          >
            {loading ? 'Сравняване...' : 'Сравни Инвентарите'}
          </button>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError('')}>×</button>
        </div>
      )}

      {copyMessage && (
        <div className="copy-message">
          <p>{copyMessage}</p>
        </div>
      )}

      {comparisonResult && (
        <div className="comparison-results">
          <div className="results-header">
            <h3>Резултати от Сравнението</h3>
            <div className="results-actions">
              {loadingZohoLinks && (
                <span className="loading-links">Зареждане на връзки към Zoho...</span>
              )}
              <button
                className="export-button"
                onClick={handleExport}
                disabled={exporting}
              >
                {exporting ? 'Експортиране...' : `Експорт ${activeFilter !== 'all' ? '(Филтрирано)' : ''}`}
              </button>
            </div>
          </div>

          <div className="results-summary">
            <div 
              className={getSummaryCardClassName('all')}
              onClick={() => handleFilterClick('all')}
            >
              <h4>Общо Елементи</h4>
              <span className="summary-number">{comparisonResult.totalItems}</span>
              {activeFilter === 'all' && <span className="active-indicator">●</span>}
            </div>
            <div 
              className={getSummaryCardClassName('match')}
              onClick={() => handleFilterClick('match')}
            >
              <h4>Точни Съвпадения</h4>
              <span className="summary-number">{comparisonResult.summary.exactMatches}</span>
              {activeFilter === 'match' && <span className="active-indicator">●</span>}
            </div>
            <div 
              className={getSummaryCardClassName('quantity_mismatch')}
              onClick={() => handleFilterClick('quantity_mismatch')}
            >
              <h4>Разлики в Количеството</h4>
              <span className="summary-number">{comparisonResult.summary.quantityMismatches}</span>
              {activeFilter === 'quantity_mismatch' && <span className="active-indicator">●</span>}
            </div>
            <div 
              className={getSummaryCardClassName('green_card_mismatch')}
              onClick={() => handleFilterClick('green_card_mismatch')}
            >
              <h4>Разлики в Зелена Карта</h4>
              <span className="summary-number">{comparisonResult.summary.greenCardMismatches}</span>
              {activeFilter === 'green_card_mismatch' && <span className="active-indicator">●</span>}
            </div>
            <div 
              className={getSummaryCardClassName('missing_items')}
              onClick={() => handleFilterClick('missing_items')}
            >
              <h4>Липсващи Елементи</h4>
              <span className="summary-number">{comparisonResult.summary.missingItems}</span>
              {activeFilter === 'missing_items' && <span className="active-indicator">●</span>}
            </div>
          </div>

          {activeFilter !== 'all' && (
            <div className="filter-info">
              <span>Показване: </span>
              <strong>
                {activeFilter === 'match' && 'Точни съвпадения'}
                {activeFilter === 'quantity_mismatch' && 'Разлики в количеството'}
                {activeFilter === 'green_card_mismatch' && 'Разлики в зелена карта'}
                {activeFilter === 'missing_items' && 'Липсващи елементи'}
              </strong>
              <button 
                className="clear-filter-btn"
                onClick={() => setActiveFilter('all')}
              >
                ×
              </button>
            </div>
          )}

          <div className="comparison-table">
            <h4>Подробно Сравнение ({getFilteredComparisons().length} елемента)</h4>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>{trackBatches ? 'Име на Артикул (Партида)' : 'Име на Артикул'}</th>
                    <th>{table1File?.name || 'Файл 1'} Количество</th>
                    <th>{table2File?.name || 'Файл 2'} Количество</th>
                    <th>Разлика</th>
                    {compareGreenCard && (
                                          <>
                      <th>{table1File?.name || 'Файл 1'} Зелена Карта</th>
                      <th>{table2File?.name || 'Файл 2'} Зелена Карта</th>
                    </>
                  )}
                  <th>Статус</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredComparisons().map((comparison, index) => (
                    <tr key={index} className={`status-${comparison.status}`}>
                      <td 
                        className="clickable-cell"
                        onClick={() => copyToClipboard(comparison.productNumber1 || comparison.batchNumber)}
                        title="Кликнете за копиране"
                      >
                        {comparison.batchNumber}
                      </td>
                      <td>{comparison.table1Quantity}</td>
                      <td>{comparison.table2Quantity}</td>
                      <td className={comparison.difference !== 0 ? 'difference' : ''}>
                        {comparison.difference}
                      </td>
                      {compareGreenCard && (
                        <>
                          <td>{comparison.greenCard1 || '-'}</td>
                          <td>{comparison.greenCard2 || '-'}</td>
                        </>
                      )}
                      <td>
                        <span className={`status-badge ${getStatusColor(comparison.status)}`}>
                          {getStatusText(comparison.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventorySync; 